import type React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  tooltip: string;
  className?: string;
  children: React.ReactNode;
}

export function IconButton({
  tooltip,
  className,
  children,
  ...buttonProps
}: IconButtonProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<{
    left: number;
    top: number;
  }>({
    left: 0,
    top: 0,
  });

  useEffect(() => {
    if (!isVisible) return;

    const updatePosition = () => {
      const button = buttonRef.current;
      const tooltipEl = tooltipRef.current;
      if (!button || !tooltipEl) return;

      const rect = button.getBoundingClientRect();
      const tooltipWidth = tooltipEl.offsetWidth;
      const tooltipHeight = tooltipEl.offsetHeight;

      const gap = 8;
      const viewportPad = 6;
      const canPlaceTop = rect.top - tooltipHeight - gap >= viewportPad;

      const rawLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
      const clampedLeft = Math.max(
        viewportPad,
        Math.min(rawLeft, window.innerWidth - tooltipWidth - viewportPad),
      );

      const top = canPlaceTop
        ? rect.top - tooltipHeight - gap
        : rect.bottom + gap;

      setTooltipStyle({ left: clampedLeft, top });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isVisible]);

  return (
    <div className="relative flex">
      <button
        {...buttonProps}
        ref={buttonRef}
        className={className}
        aria-label={buttonProps["aria-label"] ?? tooltip}
        onMouseEnter={(e) => {
          buttonProps.onMouseEnter?.(e);
          setIsVisible(true);
        }}
        onMouseLeave={(e) => {
          buttonProps.onMouseLeave?.(e);
          setIsVisible(false);
        }}
        onFocus={(e) => {
          buttonProps.onFocus?.(e);
          setIsVisible(true);
        }}
        onBlur={(e) => {
          buttonProps.onBlur?.(e);
          setIsVisible(false);
        }}
      >
        {children}
      </button>

      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className="pointer-events-none fixed z-[200] rounded-md border border-white/15 bg-gradient-to-b from-zinc-900/95 to-black/95 px-2 py-1 text-[10px] font-medium tracking-wide text-white/90 whitespace-nowrap shadow-[0_8px_24px_rgba(0,0,0,0.45)] backdrop-blur-md"
            style={{ left: tooltipStyle.left, top: tooltipStyle.top }}
            role="tooltip"
          >
            {tooltip}
          </div>,
          document.body,
        )}
    </div>
  );
}
